import { motion } from 'motion/react';
import { Mail, Linkedin, Twitter, Github, MapPin, Clock } from 'lucide-react';

export default function Contact() {
  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: 'jane@example.com',
      href: 'mailto:jane@example.com',
      description: 'Best for project inquiries and collaborations'
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: '/in/janedoe',
      href: 'https://linkedin.com/in/janedoe',
      description: 'Connect professionally and see my career journey'
    },
    {
      icon: Twitter,
      label: 'Twitter',
      value: '@janedoe_design',
      href: 'https://twitter.com/janedoe_design',
      description: 'Follow my thoughts on design and industry trends'
    },
    {
      icon: Github,
      label: 'GitHub',
      value: '/janedoe',
      href: 'https://github.com/janedoe',
      description: 'Check out my design system contributions'
    }
  ];

  const faqs = [
    {
      question: 'What type of projects do you work on?',
      answer: 'I work on a variety of digital products including mobile apps, web applications, design systems, and enterprise software. I particularly enjoy projects that involve user research, complex workflows, and accessibility challenges.'
    },
    {
      question: 'Are you available for freelance work?',
      answer: 'Yes! I take on select freelance projects, particularly those involving UX research, design strategy, or design system development. I typically work with teams for 2-6 month engagements.'
    },
    {
      question: 'Do you offer design consulting?',
      answer: 'Absolutely. I provide design strategy consulting, UX audits, design system architecture, and team mentoring services. I can help establish design processes and improve design maturity in organizations.'
    },
    {
      question: 'What\'s your typical project timeline?',
      answer: 'Project timelines vary based on scope and complexity. A typical product redesign might take 3-4 months, while a design system could take 4-6 months. I always provide detailed project timelines during the discovery phase.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 md:pt-40 pb-20 px-4"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl text-foreground mb-6">
            Let's work together
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            I'm always interested in new opportunities and collaborations. Whether you're 
            looking for a full-time designer, freelance help, or just want to chat about 
            design, I'd love to hear from you.
          </p>
        </motion.section>

        {/* Availability Status */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-700 font-medium">Available for new projects</span>
              </div>
              <p className="text-green-600 text-sm">
                Currently accepting new freelance projects and full-time opportunities starting March 2024
              </p>
            </div>
          </div>
        </motion.section>

        <div className="max-w-4xl mx-auto">
          {/* Contact Methods */}
          <motion.section
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl md:text-3xl text-foreground mb-8 text-center">Get in Touch</h2>
            
            {/* Preferred Contact Methods */}
            <div className="mb-12">
              <h3 className="text-lg text-foreground mb-6 text-center">Preferred Methods</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* WhatsApp */}
                <motion.a
                  href="https://wa.me/1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="block bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-2xl p-6 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center group-hover:bg-green-600 transition-colors">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.700"/>
                      </svg>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-medium text-green-900 dark:text-green-100 mb-1">WhatsApp</h3>
                      <p className="text-green-700 dark:text-green-300 text-sm mb-2">+1 (234) 567-8900</p>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        Quick response, perfect for urgent questions
                      </p>
                    </div>
                  </div>
                </motion.a>

                {/* Email */}
                <motion.a
                  href="mailto:hello@yourname.com"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                  className="block bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-2xl p-6 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <Mail className="w-7 h-7 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Email</h3>
                      <p className="text-blue-700 dark:text-blue-300 text-sm mb-2">hello@yourname.com</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Best for detailed project discussions
                      </p>
                    </div>
                  </div>
                </motion.a>
              </div>
            </div>

            {/* Additional Contact Options */}
            <div className="mb-12">
              <h3 className="text-lg text-foreground mb-6 text-center">Connect With Me</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* GitHub */}
                <motion.a
                  href="https://github.com/yourusername"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className="block bg-card backdrop-blur-xl border border-border rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center group-hover:bg-foreground group-hover:text-background transition-colors">
                      <Github className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">GitHub</h3>
                      <p className="text-muted-foreground text-sm mb-2">@yourusername</p>
                      <p className="text-sm text-muted-foreground">
                        Check out my code and contributions
                      </p>
                    </div>
                  </div>
                </motion.a>

                {/* Facebook */}
                <motion.a
                  href="https://facebook.com/yourprofile"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                  className="block bg-card backdrop-blur-xl border border-border rounded-2xl p-6 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <svg className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-medium mb-1">Facebook</h3>
                      <p className="text-muted-foreground text-sm mb-2">Your Name</p>
                      <p className="text-sm text-muted-foreground">
                        Follow my design journey and updates
                      </p>
                    </div>
                  </div>
                </motion.a>
              </div>
            </div>

            {/* Location & Timezone */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="grid md:grid-cols-2 gap-6"
            >
              <div className="bg-muted/30 rounded-2xl p-6 text-center">
                <MapPin className="w-6 h-6 text-purple-600 mx-auto mb-3" />
                <div className="font-medium mb-2">Location</div>
                <div className="text-muted-foreground">San Francisco, CA</div>
              </div>
              
              <div className="bg-muted/30 rounded-2xl p-6 text-center">
                <Clock className="w-6 h-6 text-purple-600 mx-auto mb-3" />
                <div className="font-medium mb-2">Timezone</div>
                <div className="text-muted-foreground">PST (UTC-8)</div>
              </div>
            </motion.div>
          </motion.section>
        </div>

        {/* CTA Section */}
        <motion.section
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl text-foreground mb-6">
            Ready to start a project?
          </h2>
          
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            I'd love to learn more about your project and discuss how we can work 
            together to create something amazing. Let's schedule a call to chat!
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="mailto:jane@example.com?subject=Project Inquiry"
              className="inline-flex items-center space-x-2 bg-foreground text-background px-8 py-4 rounded-xl hover:bg-foreground/90 transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>Send me an email</span>
            </a>
            
            <a
              href="https://calendly.com/janedoe"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 border border-border px-8 py-4 rounded-xl hover:bg-accent transition-colors"
            >
              <Clock className="w-4 h-4" />
              <span>Schedule a call</span>
            </a>
          </div>
          
          <p className="text-muted-foreground text-sm mt-6">
            Typical response time: 24-48 hours
          </p>
        </motion.section>
      </div>
    </motion.div>
  );
}
